/**
 * todayV2Demo — capa operativa de la vista previa "Hoy V2" (`/pms/today`).
 *
 * Qué añade y qué NO:
 *   - Reservas, unidades, bloqueos e incidencias siguen viniendo de
 *     `pmsDemoData` / `pmsRuntimeStore`. Llegadas, salidas, limpiezas y el
 *     tablero de unidades se DERIVAN de ahí (ver `todayV2Selectors.js`), no
 *     se declaran aquí: si el dataset cambia, Hoy cambia con él.
 *   - Aquí vive solo lo que el modelo V1 todavía no tiene: ciclo de vida de
 *     housekeeping, preparación del huésped (check-in online e inmigración),
 *     peticiones estructuradas del portal, detalle de llegada y salida, y la
 *     vista previa de rol.
 *
 * IMPORTANTE — el bloque "Necesita atención" es una REPRESENTACIÓN de demo.
 * Cuando exista backend, esos avisos NO se calculan en esta pantalla: los
 * emite el Action Center que ya existe (`GET /api/actions`,
 * `backend/actions_engine.py`) como una fuente más, con el mismo `action_id`,
 * los mismos CTA y el mismo snooze/dismiss (PRODUCT_V2_SPEC §5.2 y §16.1).
 * Hoy es una vista, nunca un motor de tareas.
 *
 * Nada de este módulo llama a ningún endpoint ni escribe en ningún sitio.
 */

// ---------------------------------------------------------------------------
// Ciclos de vida (PRODUCT_V2_SPEC §8)
// ---------------------------------------------------------------------------

/** Housekeeping — §8.1. `INSPECTED` es opcional por propiedad en el futuro. */
export const HOUSEKEEPING_FLOW = ["PENDING", "ASSIGNED", "IN_PROGRESS", "READY", "INSPECTED"];

export const HOUSEKEEPING_LABEL = {
  PENDING: "Pendiente",
  ASSIGNED: "Asignada",
  IN_PROGRESS: "En curso",
  READY: "Lista",
  INSPECTED: "Inspeccionada",
};

export const HOUSEKEEPING_TONE = {
  PENDING: "amber",
  ASSIGNED: "blue",
  IN_PROGRESS: "cyan",
  READY: "emerald",
  INSPECTED: "stone",
};

/** Tipo de tarea de limpieza — §8.1. */
export const HOUSEKEEPING_TYPE_LABEL = {
  checkout: "Salida",
  turnaround: "Salida + llegada",
  stayover: "Estancia",
  deep_clean: "Limpieza profunda",
  arrival_check: "Repaso previo a la llegada",
  request: "Petición del huésped",
};

/** Incidencias — §8.2. Los estados del dataset V1 se mapean sobre estos. */
export const ISSUE_FLOW = ["OPEN", "ASSIGNED", "IN_PROGRESS", "RESOLVED", "INSPECTED"];

export const ISSUE_STATUS_LABEL = {
  OPEN: "Abierta",
  ASSIGNED: "Asignada",
  IN_PROGRESS: "En curso",
  RESOLVED: "Resuelta",
  INSPECTED: "Verificada",
};

export const URGENCY_LABEL = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
};

// ---------------------------------------------------------------------------
// Estados del tablero de unidades
// ---------------------------------------------------------------------------

/**
 * Estado operativo de una unidad HOY. Es más fino que el `VILLA_STATE` de la
 * demo V1 porque separa "vacía y lista" de "vacía y sucia": esa diferencia se
 * deriva del estado de la tarea de limpieza, nunca de un campo aparte (§8.1).
 */
export const UNIT_STATE_LABEL = {
  OCCUPIED: "Ocupada",
  ARRIVING: "Llega hoy",
  DEPARTING: "Sale hoy",
  CLEANING: "Limpiando",
  VACANT_DIRTY: "Vacía · por limpiar",
  VACANT_READY: "Vacía · lista",
  OUT_OF_SERVICE: "Fuera de servicio",
  OWNER_STAY: "Uso del propietario",
};

export const UNIT_STATE_TONE = {
  OCCUPIED: "emerald",
  ARRIVING: "amber",
  DEPARTING: "blue",
  CLEANING: "cyan",
  VACANT_DIRTY: "amber",
  VACANT_READY: "stone",
  OUT_OF_SERVICE: "red",
  OWNER_STAY: "violet",
};

// ---------------------------------------------------------------------------
// Equipo de la demo
// ---------------------------------------------------------------------------

/**
 * Mismos nombres que ya usan los `reporter` / `assignee` de las incidencias
 * de `pmsDemoData`, para que la persona que limpia V02 sea la misma en todas
 * las pantallas. No son usuarios reales ni tienen cuenta.
 */
export const STAFF = [
  { staff_id: "hk_ratih", name: "Kadek Ratih", area: "housekeeping" },
  { staff_id: "hk_ayu", name: "Nyoman Ayu", area: "housekeeping" },
  { staff_id: "mt_wisnu", name: "Ketut Wisnu", area: "maintenance" },
  { staff_id: "mt_suarta", name: "Wayan Suarta", area: "maintenance" },
  { staff_id: "rc_adi", name: "Adi Pratama", area: "reception" },
];

export const STAFF_BY_ID = Object.fromEntries(STAFF.map((s) => [s.staff_id, s]));

export const AREA_LABEL = {
  housekeeping: "Limpieza",
  maintenance: "Mantenimiento",
  reception: "Recepción",
  services: "Servicios",
};

// ---------------------------------------------------------------------------
// Detalle de salida
// ---------------------------------------------------------------------------

/**
 * AQUÍ YA NO HAY DETALLE DE LLEGADA.
 *
 * `ARRIVAL_DETAILS` declaraba la hora prevista, el vuelo y el traslado de cada
 * llegada. Cuando llegó el modelo de huésped (§7.1) esos mismos hechos pasaron
 * a vivir en `guestsV2Demo.PRE_CHECKIN_SUBMISSIONS.arrival`, que es donde el
 * huésped los declara — y nadie retiró esta copia. Las dos discreparon:
 * para los Papadakis, `eta: "21:40"` era la hora de ATERRIZAJE (`eta_source:
 * "vuelo"`), mientras que el huésped había dicho que llegaría a la villa a las
 * 23:10. Hoy le prometía a recepción un huésped hora y media antes de que
 * pudiera aparecer.
 *
 * La llegada tiene una sola fuente: el modelo de huésped. Del resto de lo que
 * había aquí, el traslado ya era además un pedido de servicio en
 * `GUEST_REQUESTS` (§9) y el saldo sale del cálculo de dinero de la reserva;
 * la nota de llegada no la pintaba ninguna pantalla.
 */

/**
 * La salida tardía es un servicio del catálogo: el portal del huésped podrá
 * pedirla o comprarla cuando la disponibilidad lo permita (§7.2, §9). Aquí se
 * ven los dos desenlaces posibles, que es justo lo que hay que enseñar:
 * concedida cuando no hay nadie detrás, denegada cuando llega otro huésped.
 */
export const DEPARTURE_DETAILS = {
  R2028005: {
    late_checkout: {
      requested_via: "guest_portal",
      status: "denied",
      reason: "Llega otro huésped hoy a la hora de entrada.",
    },
  },
  R2028011: {
    late_checkout: {
      requested_via: "guest_portal",
      status: "granted",
      until: "13:00",
      price_eur: 25,
      reason: "Sin llegada detrás · cobrada en el portal.",
    },
  },
};

// ---------------------------------------------------------------------------
// Preparación del huésped (§7.4)
// ---------------------------------------------------------------------------

/**
 * Solo ESTADO. Nunca el contenido del documento: la vista previa no enseña ni
 * un número de pasaporte ni una imagen, y el producto real tampoco lo hará
 * fuera del ámbito `guest_document` (§7.3).
 *
 * `immigration` cubre el reporte de extranjeros (APOA en Indonesia). Es una
 * cola operativa: el producto prepara y exporta, la propiedad presenta. No
 * hay ni habrá integración con ningún sistema gubernamental por ahora.
 */
export const GUEST_READINESS = {
  R2028006: {
    pre_checkin: "in_progress",
    fields_done: { passport: false, arrival_time: false, nationality: true, phone: true },
    immigration: "due",
  },
  R2028009: {
    pre_checkin: "completed",
    fields_done: { passport: true, arrival_time: true, nationality: true, phone: true },
    immigration: "due",
  },
  R2028015: {
    pre_checkin: "completed",
    fields_done: { passport: true, arrival_time: true, nationality: true, phone: true },
    immigration: "filed",
  },
  R2028024: {
    pre_checkin: "completed",
    fields_done: { passport: true, arrival_time: true, nationality: true, phone: true },
    immigration: "due",
  },
};

export const PRE_CHECKIN_LABEL = {
  not_started: "Sin empezar",
  in_progress: "A medias",
  completed: "Completado",
  verified: "Verificado",
};

export const IMMIGRATION_LABEL = {
  due: "Pendiente de presentar",
  filed: "Presentado",
  not_required: "No aplica",
};

// ---------------------------------------------------------------------------
// Peticiones estructuradas del portal del huésped (§7.2, §9.2)
// ---------------------------------------------------------------------------

/**
 * Lo importante del modelo: una petición del huésped NO es un mensaje suelto
 * de WhatsApp. Es un registro con tipo, unidad, hora, estado y área operativa
 * responsable, y por eso puede convertirse en trabajo (una tarea de limpieza,
 * una incidencia de mantenimiento, un pedido de servicio) y contarse después.
 *
 * `label_template` lleva `{unidad}` donde aparece el vocabulario configurado
 * en Ajustes: si la propiedad llama "habitación" a su unidad, la petición se
 * llama "Limpiar mi habitación".
 */
export const REQUEST_TYPES = [
  { key: "fresh_towels", label: "Toallas limpias", area: "housekeeping", icon: "TShirt", creates: "housekeeping_task" },
  { key: "clean_unit", label_template: "Limpiar mi {unidad}", area: "housekeeping", icon: "Broom", creates: "housekeeping_task" },
  { key: "amenities", label: "Amenities", area: "housekeeping", icon: "Basket", creates: "housekeeping_task" },
  { key: "airport_pickup", label: "Recogida en el aeropuerto", area: "reception", icon: "Car", service_id: "svc_airport", creates: "service_order" },
  { key: "scooter", label: "Entrega de scooter", area: "reception", icon: "Scooter", service_id: "svc_scooter", creates: "service_order" },
  { key: "breakfast", label: "Desayuno", area: "services", icon: "ForkKnife", service_id: "svc_breakfast", creates: "service_order" },
  { key: "welcome_pack", label: "Welcome pack", area: "services", icon: "Gift", service_id: "svc_welcome", creates: "service_order" },
  { key: "late_checkout", label: "Salida tardía", area: "reception", icon: "Clock", creates: "service_order" },
  { key: "report_something", label: "Reportar algo", area: "maintenance", icon: "Warning", creates: "maintenance_issue", free_text: true },
];

export const REQUEST_TYPE_BY_KEY = Object.fromEntries(REQUEST_TYPES.map((t) => [t.key, t]));

/** Estados del pedido de servicio — §9.2. */
export const REQUEST_FLOW = ["requested", "confirmed", "in_progress", "delivered"];

export const REQUEST_STATUS_LABEL = {
  requested: "Recibida",
  confirmed: "Confirmada",
  in_progress: "En curso",
  delivered: "Entregada",
  cancelled: "Cancelada",
};

export const REQUEST_STATUS_TONE = {
  requested: "amber",
  confirmed: "blue",
  in_progress: "cyan",
  delivered: "emerald",
  cancelled: "stone",
};

export const GUEST_REQUESTS = [
  {
    request_id: "GR2028001",
    type: "report_something",
    reservation_id: "R2028027",
    villa_id: "V09",
    requested_at: "07:50",
    status: "in_progress",
    urgency: "medium",
    description: "El grifo de la ducha principal gotea.",
    issue_id: "I2028002",
    assignee: "mt_wisnu",
  },
  {
    request_id: "GR2028002",
    type: "fresh_towels",
    reservation_id: "R2028003",
    villa_id: "V01",
    requested_at: "08:40",
    status: "requested",
    quantity: 4,
    assignee: null,
  },
  {
    request_id: "GR2028003",
    type: "clean_unit",
    reservation_id: "R2028030",
    villa_id: "V10",
    requested_at: "09:05",
    status: "in_progress",
    scheduled_for: "12:30",
    assignee: "hk_ayu",
  },
  {
    request_id: "GR2028004",
    type: "amenities",
    reservation_id: "R2028003",
    villa_id: "V01",
    requested_at: "09:20",
    status: "delivered",
    assignee: "hk_ratih",
  },
  {
    request_id: "GR2028005",
    type: "airport_pickup",
    reservation_id: "R2028024",
    villa_id: "V08",
    requested_at: "ayer · 18:20",
    status: "requested",
    scheduled_for: "20:45",
    assignee: null,
  },
  {
    request_id: "GR2028006",
    type: "welcome_pack",
    reservation_id: "R2028024",
    villa_id: "V08",
    requested_at: "ayer · 18:22",
    status: "requested",
    scheduled_for: "antes de las 20:00",
    assignee: null,
  },
  {
    request_id: "GR2028007",
    type: "scooter",
    reservation_id: "R2028015",
    villa_id: "V05",
    requested_at: "ayer · 21:40",
    status: "confirmed",
    scheduled_for: "hoy · 14:00",
    quantity: 2,
    assignee: "rc_adi",
  },
  {
    request_id: "GR2028008",
    type: "breakfast",
    reservation_id: "R2028021",
    villa_id: "V07",
    requested_at: "ayer · 22:10",
    status: "confirmed",
    scheduled_for: "mañana · 08:00",
    quantity: 7,
    assignee: null,
  },
  {
    request_id: "GR2028009",
    type: "late_checkout",
    reservation_id: "R2028011",
    villa_id: "V04",
    requested_at: "ayer · 19:05",
    status: "delivered",
    scheduled_for: "13:00",
    assignee: "rc_adi",
  },
];

// ---------------------------------------------------------------------------
// Housekeeping — capa V2 sobre las tareas derivadas del día
// ---------------------------------------------------------------------------

/**
 * Estado y responsable de cada tarea del día. La tarea en sí (qué unidad,
 * si hay salida, si hay llegada) la deriva `housekeepingTasksForDay`; aquí
 * solo se dice en qué punto del ciclo está y quién la lleva.
 */
export const HOUSEKEEPING_SEED = {
  V02: { status: "IN_PROGRESS", assignee: "hk_ratih" },
  V03: { status: "PENDING", assignee: null, deep_clean_issue_id: "I2028004" },
  V04: { status: "PENDING", assignee: "hk_ayu" },
  V05: { status: "READY", assignee: "hk_ratih" },
  V08: { status: "INSPECTED", assignee: "hk_ayu" },
};

// ---------------------------------------------------------------------------
// Secciones y vista previa de rol
// ---------------------------------------------------------------------------

/**
 * Orden conceptual de Hoy (PRODUCT_V2_SPEC §5.1). En móvil este es
 * literalmente el orden de la página; en escritorio se reparte en dos
 * columnas sin cambiar la prioridad.
 */
export const TODAY_SECTIONS = [
  { key: "attention", label: "Necesita atención" },
  { key: "snapshot", label: "Resumen del día" },
  { key: "arrivals", label: "Llegadas" },
  { key: "departures", label: "Salidas" },
  { key: "housekeeping", label: "Limpieza" },
  { key: "issues", label: "Incidencias" },
  { key: "readiness", label: "Preparación del huésped" },
  { key: "requests", label: "Peticiones y servicios" },
  { key: "board", label: "Estado de las unidades" },
];

/**
 * Cómo se simplificaría Hoy por rol (§14, §15.2). Es una MAQUETA: no toca
 * la autenticación, no cambia permisos y no sustituye a ningún guard. Sirve
 * para enseñar que quien limpia no necesita ver ni finanzas ni datos
 * personales del huésped.
 */
export const ROLE_PREVIEWS = [
  {
    role_key: "property_admin",
    label: "Administrador",
    summary: "Ve todo el día completo.",
    sections: TODAY_SECTIONS.map((s) => s.key),
    guest_names: true,
    attention_sources: null,
  },
  {
    role_key: "reception",
    label: "Recepción",
    summary: "Llegadas, salidas, huéspedes y peticiones.",
    sections: ["attention", "snapshot", "arrivals", "departures", "readiness", "requests", "board"],
    guest_names: true,
    attention_sources: ["reservation", "guest", "service"],
  },
  {
    role_key: "housekeeping",
    label: "Housekeeping",
    summary: "Sus limpiezas y el estado de las unidades. Sin datos del huésped.",
    sections: ["attention", "snapshot", "housekeeping", "board"],
    guest_names: false,
    attention_sources: ["housekeeping"],
  },
  {
    role_key: "maintenance",
    label: "Mantenimiento",
    summary: "Incidencias y unidades bloqueadas. Sin datos del huésped.",
    sections: ["attention", "snapshot", "issues", "board"],
    guest_names: false,
    attention_sources: ["maintenance"],
  },
];

export const ROLE_PREVIEW_BY_KEY = Object.fromEntries(ROLE_PREVIEWS.map((r) => [r.role_key, r]));

export const DEFAULT_ROLE_PREVIEW = "property_admin";

// ---------------------------------------------------------------------------
// Avisos — vocabulario del Action Center existente
// ---------------------------------------------------------------------------

/**
 * Se reutiliza tal cual la escala de urgencia de `actions_engine.py` para que
 * el día que estos avisos vengan del Action Center no haya que traducir nada.
 */
export const URGENCY_ORDER = ["CRITICAL", "OVERDUE", "TODAY", "UPCOMING", "INFO"];

export const URGENCY_TAG = {
  CRITICAL: { label: "Crítico", tone: "red" },
  OVERDUE: { label: "Vencido", tone: "red" },
  TODAY: { label: "Atención", tone: "amber" },
  UPCOMING: { label: "Próximo", tone: "blue" },
  INFO: { label: "Info", tone: "stone" },
};

/** Aviso de alcance, visible en la pantalla: nada de esto es producción. */
export const TODAY_DEMO_NOTE =
  "Prototipo del futuro Hoy. Los avisos son una representación: con backend "
  + "los emitirá el Action Center, no esta pantalla.";
